import React, { useState, useRef } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import Button from '../../../../../../../ui/canon/Button';
import Stepper from '../../../../../../../ui/canon/Stepper';
import { CategoryTemplate } from '../../../../../../../ui/layouts/CategoryTemplate';
import SuccessionSellerInfo from './SuccessionSellerInfo';
import SuccessionNumberOfBuyers from './SuccessionNumberOfBuyers';
import SuccessionBuyer from './SuccessionBuyer';
import SuccessionDealStructure from './SuccessionDealStructure';
import SuccessionDownPayment from './SuccessionDownPayment';
import SuccessionFixedPayment from './SuccessionFixedPayment';
import SuccessionContinuingPayments from './SuccessionContinuingPayments';
import SuccessionDisclaimer from './SuccessionDisclaimer';

const STEP_LENGTH = 8;

const UPDATE_CFA_MUTATION = gql`
  mutation updateCfa(
    $id: ID, 
    $name: String, 
    $cashFlowLength: Int, 
    $cashFlowProduction: Int,
    $cashFlowProductionShift: Float,
    $sellerT12Revenue: Float,
    $sellerFiveYearGrowthRate: Float,
    $sellerManualTaxRate: Boolean,
    $sellerTaxRate: Float,
    $numberOfBuyers: Int,
    $buyer1T12Revenue: Float,
    $buyer1FiveYearGrowthRate: Float,
    $buyer1PercentagePurchasing: Float,
    $buyer1ManualTaxRate: Boolean,
    $buyer1TaxRate: Float,
    $buyer1AnnualExpenses: Float,
    $buyer1ExpenseGrowth: Float,
    $buyer2T12Revenue: Float,
    $buyer2FiveYearGrowthRate: Float,
    $buyer2PercentagePurchasing: Float,
    $buyer2ManualTaxRate: Boolean,
    $buyer2TaxRate: Float,
    $buyer2AnnualExpenses: Float,
    $buyer2ExpenseGrowth: Float,
    $buyerHasLumpSumPayment: Boolean,
    $buyerLumpSumAmount: Float,
    $buyerLumpSumYear: Int,
    $buyerIsFinancing: Boolean,
    $buyersFinancingAmount: Float,
    $buyerFinancingYears: Float,
    $cashFlowFinancingInterestRate: Float,
    $buyerHasFixedPayments: Boolean,
    $buyerFixedPaymentYears: Int,
    $buyerFixedPaymentStartYear: Int,
    $buyerFixedPaymentAmount: Float,
    $buyerHasContinuingPayments: Boolean,
    $buyerContinuingPaymentYears: Int,
    $buyerContinuingPaymentStartYear: Int,
    $buyerContinuingPayments: [Float!],
    $hasCheckSwap: Boolean,
    $checkSwapYear: Int,
    $checkSwapAnnualBonus: Float,
    $checkSwapLoanEndYear: Int,
    $checkSwapLoanRepaymentAmount: Float,
    $consultingTotalCompensation: Float,
    $consultingLength: Int,
    $bdRetentionRate: Float,
    $sellerNoteAmount: Float,
    $sellerNoteTerm: Int,
    $sellerNoteInterestRate: Float,
    $cashflowSellerLifetimeCapitalGainsTax: Float
  ) {
    updateCfa(
      id: $id, 
      name: $name, 
      cash_flow_length: $cashFlowLength,
      cash_flow_production: $cashFlowProduction,
      cash_flow_production_shift: $cashFlowProductionShift,
      seller_t12_revenue: $sellerT12Revenue,
      seller_five_year_growth_rate: $sellerFiveYearGrowthRate,
      seller_manual_tax_rate: $sellerManualTaxRate,
      seller_tax_rate: $sellerTaxRate,
      number_of_buyers: $numberOfBuyers,
      buyer_1_t12_revenue: $buyer1T12Revenue,
      buyer_1_five_year_growth_rate: $buyer1FiveYearGrowthRate,
      buyer_1_percentage_purchasing: $buyer1PercentagePurchasing,
      buyer_1_manual_tax_rate: $buyer1ManualTaxRate,
      buyer_1_tax_rate: $buyer1TaxRate,
      buyer_1_annual_expenses: $buyer1AnnualExpenses,
      buyer_1_expense_growth: $buyer1ExpenseGrowth,
      buyer_2_t12_revenue: $buyer2T12Revenue,
      buyer_2_five_year_growth_rate: $buyer2FiveYearGrowthRate,
      buyer_2_percentage_purchasing: $buyer2PercentagePurchasing,
      buyer_2_manual_tax_rate: $buyer2ManualTaxRate,
      buyer_2_tax_rate: $buyer2TaxRate,
      buyer_2_annual_expenses: $buyer2AnnualExpenses,
      buyer_2_expense_growth: $buyer2ExpenseGrowth,
      buyer_has_lump_sum_payment: $buyerHasLumpSumPayment,
      buyer_lump_sum_amount: $buyerLumpSumAmount,
      buyer_lump_sum_year: $buyerLumpSumYear,
      buyer_has_financing: $buyerIsFinancing,
      buyers_financing_amount: $buyersFinancingAmount,
      buyer_financing_years: $buyerFinancingYears,
      cash_flow_financing_interest_rate: $cashFlowFinancingInterestRate,
      buyer_has_fixed_payments: $buyerHasFixedPayments,
      buyer_fixed_payment_years: $buyerFixedPaymentYears,
      buyer_fixed_payment_start_year: $buyerFixedPaymentStartYear,
      buyer_fixed_payment_amount: $buyerFixedPaymentAmount,
      buyer_has_continuing_payments: $buyerHasContinuingPayments,
      buyer_continuing_payment_years: $buyerContinuingPaymentYears,
      buyer_continuing_payment_start_year: $buyerContinuingPaymentStartYear,
      buyer_continuing_payments: $buyerContinuingPayments,
      has_check_swap: $hasCheckSwap,
      check_swap_year: $checkSwapYear,
      check_swap_annual_bonus: $checkSwapAnnualBonus,
      check_swap_loan_end_year: $checkSwapLoanEndYear,
      check_swap_loan_repayment_amount: $checkSwapLoanRepaymentAmount,
      consulting_total_compensation: $consultingTotalCompensation,
      consulting_length: $consultingLength,
      bd_retention_rate: $bdRetentionRate,
      seller_note_amount: $sellerNoteAmount,
      seller_note_term: $sellerNoteTerm,
      seller_note_interest_rate: $sellerNoteInterestRate,
      cashflow_seller_lifetime_capital_gains_tax: $cashflowSellerLifetimeCapitalGainsTax
    ) {
      id
      name
    }
  }
`;

const SuccessionCfaWizard = ({ currentUser, intl }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  const [query, setQuery] = useState({
    valid: false,
    sellerT12Revenue: '',
    sellerFiveYearGrowthRate: 0,
    sellerManualTaxRate: false,
    sellerTaxRate: 0.35,
    numberOfBuyers: 1,
    cashFlowLength: 6,
    name: '',
    cashFlowProductionShift: 3,
    // Down payment defaults
    buyerHasLumpSumPayment: false,
    buyerIsFinancing: false,
    hasCheckSwap: false,
    hasConsultingAgreement: false,
    // Fixed payment defaults
    buyerHasFixedPayments: false,
    // Continuing payment defaults
    buyerHasContinuingPayments: false,
    // Missing fields that Ruby service expects
    cashFlowProduction: 100,
    cashFlowFinancingInterestRate: 0.05,
    buyer1AnnualExpenses: 0,
    buyer2AnnualExpenses: 0,
    buyer1ExpenseGrowth: 0,
    buyer2ExpenseGrowth: 0,
    buyer1ManualTaxRate: false,
    buyer1TaxRate: 0.35,
    buyer2ManualTaxRate: false,
    buyer2TaxRate: 0.35,
    buyer1FiveYearGrowthRate: 0,
    buyer2FiveYearGrowthRate: 0,
    bdRetentionRate: 1.0,
    sellerNoteAmount: 0,
    sellerNoteTerm: 0,
    sellerNoteInterestRate: 0,
    cashflowSellerLifetimeCapitalGainsTax: 0
  });
  
  const currentStepRef = useRef();
  const features = currentUser?.company?.features || [];
  const cashFlowOrdinaryIncomeTaxRates = features.includes('cash_flow_ordinary_income_tax_rates');
  const cashFlowAnalysisWord = intl.locale === 'fr'
    ? (currentUser?.company?.cash_flow_analysis_word_fr || 'Analyse des flux de trésorerie')
    : (currentUser?.company?.cash_flow_analysis_word || 'Cash Flow Analysis');

  // Apollo mutation hook
  const [updateCfa, { loading: mutationLoading }] = useMutation(UPDATE_CFA_MUTATION);

  const steps = [
    intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.step_participants' }),
    intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.step_buyers' }),
    intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.step_participants' }),
    intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.step_terms' }),
    intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.step_down_payment' }),
    intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.step_fixed_payments' }),
    intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.step_continuing_payments' }),
    intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.step_disclaimer' }),
  ];

  const updateCfaQuery = (newQuery) => {
    setQuery({ ...query, ...newQuery });
  };

  const totalSteps = () => STEP_LENGTH;

  const completedSteps = () => Object.keys(completed).length;

  const isLastStep = () => activeStep === totalSteps() - 1;

  const allStepsCompleted = () => completedSteps() === totalSteps();

  const handleNext = () => {
    let newActiveStep = activeStep + 1;

    if (!cashFlowOrdinaryIncomeTaxRates && newActiveStep === 2) {
      newActiveStep += 1;
    }

    if (isLastStep() && !allStepsCompleted()) {
      newActiveStep = steps.findIndex((step, i) => !(i in completed));
    }

    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    let newActiveStep = activeStep - 1;

    if (!cashFlowOrdinaryIncomeTaxRates && newActiveStep === 2) {
      newActiveStep -= 1;
    }

    setActiveStep(newActiveStep);
  };

  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  const handleComplete = () => {
    const newCompleted = { ...completed };
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
    handleNext();
  };

  const handleReset = () => {
    setActiveStep(0);
    setCompleted({});
  };

  const currentStep = (step) => {
    switch(step) {
      case 1:
        return SuccessionNumberOfBuyers;
      case 2:
        return SuccessionBuyer;
      case 3:
        return SuccessionDealStructure;
      case 4:
        return SuccessionDownPayment;
      case 5:
        return SuccessionFixedPayment;
      case 6:
        return SuccessionContinuingPayments;
      case 7:
        return SuccessionDisclaimer;
      default:
        return SuccessionSellerInfo;
    }
  };

  const activeStepToDisplay = () => {
    return activeStep;
  };

  const CurrentStepComponent = currentStep(activeStep);

  // Function to provide helpful tips for each step
  const getStepTip = (step) => {
    const tips = [
      intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.tip_participants' }),
      intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.tip_buyers' }),
      intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.tip_terms' }),
      intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.tip_down_payment' }),
      intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.tip_fixed_payments' }),
      intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.tip_continuing_payments' }),
      intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.tip_disclaimer' }),
    ];
    return tips[step] || intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.tip_default' });
  };

  const sidebarContent = (
    <div className="space-y-6">
      {/* Progress Summary */}
      <div className="bg-[var(--succession-bg-secondary)] rounded-lg shadow-sm p-6 border border-[var(--succession-border)]">
        <h3 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2" style={{ color: 'var(--succession-primary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 00-2-2z" />
          </svg>
          {intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.progress_summary' })}
        </h3>
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-[var(--succession-text-secondary)]">{intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.current_step' })}</span>
            <span className="font-semibold" style={{ color: 'var(--succession-primary)' }}>{intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.step_of' }, { current: activeStep + 1, total: steps.length })}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[var(--succession-text-secondary)]">{intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.completion' })}</span>
            <span className="font-semibold text-green-600">{intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.completion_pct' }, { pct: Math.round(((activeStep + 1) / steps.length) * 100) })}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[var(--succession-text-secondary)]">{intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.estimated_time' })}</span>
            <span className="font-semibold text-[var(--succession-text-primary)]">{intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.time_remaining' }, { minutes: Math.max(1, steps.length - activeStep - 1) })}</span>
          </div>
        </div>
      </div>

      {/* Current Step Tip */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg shadow-sm p-6 border border-amber-100">
        <h3 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-3 flex items-center">
          <svg className="w-5 h-5 mr-2 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          {intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.step_tip' })}
        </h3>
        <p className="text-sm text-[var(--succession-text-secondary)] leading-relaxed">
          {getStepTip(activeStep)}
        </p>
      </div>

      {/* Quick Actions */}
      <div className="bg-[var(--succession-bg-primary)] rounded-lg shadow-sm p-6 border border-[var(--succession-border)]">
        <h3 className="text-lg font-semibold text-[var(--succession-text-primary)] mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-[var(--succession-text-secondary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          {intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.quick_actions' })}
        </h3>
        <div className="space-y-3">
          <a 
            href="/succession/calculators/cfa"
            className="group flex items-center w-full px-4 py-3 text-center bg-[var(--succession-bg-secondary)] rounded-lg hover:bg-[var(--succession-hover-bg)] transition-all duration-200 transform hover:scale-105"
            style={{ color: 'var(--succession-primary)' }}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            {intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.view_library' })}
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
      
      <CategoryTemplate
        variant="wide"
        sidebar={sidebarContent}
      >
        {/* Enhanced Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full shadow-lg mb-4" style={{ background: 'var(--succession-primary)' }}>
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 00-2-2z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-[var(--succession-text-primary)] mb-3">
            <FormattedMessage 
              id="calculators.cfa.title" 
              values={{ cash_flow_analysis_word: cashFlowAnalysisWord }}
            />
          </h1>
          <p className="text-lg text-[var(--succession-text-secondary)] max-w-2xl mx-auto">
            {intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.description' })}
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-[var(--succession-bg-primary)] rounded-xl shadow-xl p-8 border border-[var(--succession-border)] backdrop-blur-sm">
              {/* Progress Steps */}
              <Stepper
                steps={steps.map((label, index) => ({ label, id: index }))}
                activeStep={activeStepToDisplay()}
                completedSteps={Array.from({ length: activeStepToDisplay() }, (_, i) => i)}
                onStepClick={(index) => setActiveStep(index)}
                showProgressBar={true}
                className="mb-8"
                stepLabel={intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.step_of' }, { current: activeStepToDisplay() + 1, total: steps.length })}
                completeLabel={intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.completion_pct' }, { pct: Math.round(((activeStepToDisplay() + 1) / steps.length) * 100) })}
              />

              {/* Step Content - Enhanced with transitions */}
              <div className="mb-8">
                <div className="bg-[var(--succession-bg-secondary)] rounded-xl p-6 border border-[var(--succession-border)] shadow-sm">
                  <div className="animate-fadeIn">
                    <CurrentStepComponent 
                      query={query} 
                      updateCfaQuery={updateCfaQuery}
                      currentUser={currentUser}
                    />
                  </div>
                </div>
              </div>

              {/* Navigation Buttons - Enhanced with better styling */}
              <div className="flex items-center justify-between pt-6 border-t border-[var(--succession-border)]">
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  variant="ghost"
                  size="lg"
                  className="group transform hover:scale-105 active:scale-95"
                >
                  <svg className="w-5 h-5 mr-2 transition-transform duration-200 group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <FormattedMessage id="calculators.cfa.back"/>
                </Button>

                {/* Progress indicator in the middle */}
                <div className="flex items-center space-x-2">
                  {steps.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index <= activeStep ? 'scale-125' : 'bg-[var(--succession-border)]'
                      }`}
                      style={index <= activeStep ? { backgroundColor: 'var(--succession-primary)' } : {}}
                    />
                  ))}
                </div>

                {activeStep !== totalSteps() - 1 ? (
                  <Button 
                    disabled={!query.valid} 
                    onClick={handleComplete}
                    variant="primary"
                    size="lg"
                    className="group transform hover:scale-105 active:scale-95"
                  >
                    <FormattedMessage id="calculators.cfa.next"/>
                    <svg className="w-5 h-5 ml-2 transition-transform duration-200 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Button>
                ) : (
                  <Button
                    disabled={mutationLoading}
                    onClick={() => {
                      updateCfa({ variables: query }).then(({ data }) => {
                        const cashFlowId = data?.updateCfa?.friendlyName || data?.updateCfa?.id;
                        window.location.href = `/succession/calculators/cfa/${cashFlowId}`;
                      });
                    }}
                    variant="primary"
                    size="lg"
                    className="group transform hover:scale-105 active:scale-95 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:transform-none"
                  >
                    {mutationLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {intl.formatMessage({ id: 'succession.cash_flow_analysis.wizard.generating' })}
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <FormattedMessage id="calculators.cfa.generate"/>
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
      </CategoryTemplate>
    </>
  );
};

export default injectIntl(SuccessionCfaWizard); 